import { createRouter, createWebHistory } from 'vue-router'
import { ROLE_ADMIN, OSD_PROCESSING_ROLES } from './constants/roles'

// BBatts: meta.roles = roles allowed to visit the route (any match passes).
// Routes without meta.roles only require being logged in.
const adminOnly = { roles: [ROLE_ADMIN] }
const osdProcessing = { roles: OSD_PROCESSING_ROLES }

const routes = [
  {
    path: '/',
    name: 'dashboard',
    component: () => import('./components/Dashboard.vue'),
  },
  {
    path: '/login',
    name: 'login',
    components: { default: () => import('./pages/auth/LoginPage.vue') },
  },
  {
    // Entry point from Aurora's launcher: starts the redirect immediately so the user never
    // sees a login screen. Public, for the same reason as the callback below.
    path: '/auth/sso',
    name: 'aurora-start',
    components: { default: () => import('./pages/auth/AuroraStart.vue') },
  },
  {
    // Where Aurora redirects back to after a successful sign-in. Must stay public: the
    // guard below runs before this component can exchange the code for a session.
    path: '/auth/callback',
    name: 'aurora-callback',
    components: { default: () => import('./pages/auth/AuroraCallback.vue') },
  },
  {
    path: '/logout',
    name: 'logout',
    component: {
      // @ts-ignore
      beforeRouteEnter(to, from, next) {
        console.log({ from })
        const destination = {
          path: from.path || '/',
          query: from.query,
          params: from.params,
        }
        if (!from) {
          console.log('no from')
        }
        localStorage.clear()

        next(destination)
      },
    },
  },
  {
    path: '/pros/list',
    name: 'proList',
    components: { default: () => import('./pages/pros/ProListPage.vue') },
  },
  {
    path: '/pros/detail/:id',
    name: 'proDetails',
    props: true,
    components: { default: () => import('./pages/pros/ProDetailPage.vue') },
  },
  {
    path: '/manifests/list',
    name: 'manifestList',
    components: { default: () => import('./pages/manifests/ManifestListPage.vue') },
  },
  {
    path: '/manifests/pickup/new',
    name: 'manifestNewPickup',
    components: { default: () => import('./pages/manifests/ManifestNewPage.vue') },
  },
  {
    path: '/manifests/detail/:id',
    name: 'manifestDetails',
    props: true,
    components: { default: () => import('./pages/manifests/ManifestDetailPage.vue') },
  },
  {
    path: '/manifests/audit/:id',
    name: 'manifestAudit',
    props: true,
    components: { default: () => import('./pages/manifests/ManifestAuditPage.vue') },
  },
  {
    path: '/manifests/print/:id',
    name: 'manifestPrint',
    props: true,
    components: { default: () => import('./pages/manifests/ManifestPrintPage.vue') },
  },
  {
    path: '/corporate/postalcodes/list',
    name: 'postalCodes',
    components: { default: () => import('./pages/postal_code/PostalCodeListPage.vue') },
    meta: adminOnly,
  },
  {
    path: '/corporate/terminals/list',
    name: 'TerminalsList',
    components: { default: () => import('./pages/terminals/TerminalsListPage.vue') },
    meta: adminOnly,
  },
  {
    path: '/corporate/doors/list',
    name: 'DoorsList',
    components: { default: () => import('./pages/doors/DoorsListPage.vue') },
    meta: adminOnly,
  },
  {
    path: '/corporate/locations/list',
    name: 'locationsList',
    components: { default: () => import('./pages/locations/LocationsPage.vue') },
    meta: adminOnly,
  },
  {
    path: '/corporate/lanes/list',
    name: 'lanesList',
    components: { default: () => import('./pages/lanes/LanesPage.vue') },
    meta: adminOnly,
  },
  {
    path: '/corporate/lanes/:id?',
    name: 'lanesNew',
    props: true,
    components: { default: () => import('./pages/lanes/NewLanePage.vue') },
    meta: adminOnly,
  },
  {
    path: '/corporate/integrations',
    name: 'integrations',
    components: { default: () => import('./pages/admin/IntegrationsPage.vue') },
    meta: adminOnly,
  },
  {
    path: '/admin/line-items',
    name: 'adminLineItems',
    components: { default: () => import('./pages/admin/AdminLineItemsPage.vue') },
    meta: adminOnly,
  },
  {
    path: '/corporate/zones/list',
    name: 'zonesList',
    components: { default: () => import('./pages/zones/ZonesListPage.vue') },
    meta: adminOnly,
  },
  {
    path: '/corporate/zones/:id?',
    name: 'zonesNew',
    props: true,
    components: { default: () => import('./pages/zones/NewZonePage.vue') },
    meta: adminOnly,
  },
  {
    path: '/corporate/users/list',
    name: 'usersList',
    props: true,
    components: { default: () => import('./pages/users/UsersListPage.vue') },
    meta: adminOnly,
  },
  {
    path: '/inventory/list',
    name: 'inventoryList',
    props: false,
    components: { default: () => import('./pages/inventory/InventoryListPage.vue') },
  },
  {
    path: '/inventory/maintenance',
    name: 'inventoryJob',
    props: false,
    components: { default: () => import('./pages/inventory/InventoryJob.vue') },
  },
  {
    path: '/inventory/terminals',
    name: 'inventory-terminals',
    components: { default: () => import('./pages/inventory/InventoryTerminal.vue') },
  },
  {
    path: '/orphans',
    name: 'orphans',
    components: { default: () => import('./pages/orphans/OrphanList.vue') },
  },
  {
    path: '/search',
    name: 'globalSearch',
    components: { default: () => import('./pages/search/GlobalSearchPage.vue') },
  },
  {
    path: '/osd/queue',
    name: 'osdWorkQueue',
    components: { default: () => import('./pages/osd/OsdWorkQueuePage.vue') },
    meta: osdProcessing,
  },
  {
    path: '/osd/destroyed',
    name: 'osdDestroyed',
    components: { default: () => import('./pages/osd/DestroyedItemsPage.vue') },
    meta: osdProcessing,
  },
  {
    path: '/osd/counts',
    name: 'osdCounts',
    components: { default: () => import('./pages/osd/OsdCountsReportPage.vue') },
    meta: osdProcessing,
  },
  {
    path: '/orphans/list',
    name: 'orphanList',
    component: () => import('./pages/orphans/OrphanList.vue'),
  },
  {
    path: '/orphans/:barcode',
    name: 'orphanDetail',
    component: () => import('./pages/orphans/OrphanDetail.vue'),
    props: true,
  },
  {
    path: '/trackingunits/lookup',
    name: 'trackingunitsLookup',
    props: false,
    components: { default: () => import('./pages/trackingunits/TrackingUnitsLookup.vue') },
  },
  {
    path: '/reports/terminalproductivity',
    name: 'terminalproductivity',
    props: false,
    components: { default: () => import('./pages/reports/TerminalProductivity.vue') },
  },
]

const router = createRouter({
  history: createWebHistory(),
  base: window.config?.VITE_BASE_URL,
  // @ts-ignore
  routes,
});

router.beforeEach((to, _from, next) => {
  const publicPages = ['/login', '/auth/sso', '/auth/callback'];
  const authRequired = !publicPages.includes(to.path);
  let loggedIn = JSON.parse(localStorage.getItem('user'));

  if (loggedIn && Date.parse(loggedIn?.expiration) < Date.now()) {
    localStorage.removeItem('user');
    loggedIn = false;
  }
  // BBatts: a session stored before roles were added to the login response has no `roles`
  // array at all. The role guard below read that as "holds no roles", which silently locked
  // admins out of every gated route until their token expired. Treat it as a stale session and
  // make them re-authenticate instead. A user who genuinely holds no roles still gets
  // `roles: []` from the API, so this does not loop them back to login.
  if (loggedIn && !Array.isArray(loggedIn.roles)) {
    localStorage.removeItem('user');
    loggedIn = false;
  }
  if (to.path == '/logout') {
    loggedIn = false;
  }

  // Keep Vuex auth state in sync with localStorage. Without this, App.vue's
  // top-bar / menu stay visible on the login screen because the store still
  // thinks the user is logged in even after the token expired or the user
  // hit /logout.
  if (!loggedIn && router.app?.$store?.state?.auth?.status?.loggedIn) {
    router.app.$store.dispatch('auth/logout');
  }

  // trying to access a restricted page + not logged in
  // redirect to login page
  if (authRequired && !loggedIn) {
    next('/login');
    return;
  }

  // BBatts: role-gated routes (meta.roles) bounce non-matching users to "/".
  // Reads localStorage directly so a hard refresh can't race store hydration.
  const allowedRoles = to.meta?.roles;
  if (allowedRoles && loggedIn) {
    const userRoles = loggedIn.roles ?? [];
    if (!allowedRoles.some((role) => userRoles.includes(role))) {
      next('/');
      return;
    }
  }

  next();
});

// Bind the running app instance so the guard above can reach the store.
router.bindApp = (app) => {
  router.app = app;
};

export default router;
